import { CheckoutSession, MerchantStructure } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { useCart } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useCart';
import { ItemMap } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/maps/ItemMap';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';
import MerchantStructureEntity from '@/entities/MerchantStructureEntity';
import { useGetMerchantStructureById } from '@/hooks/requests/merchant/useGetMerchantStructureById';

export const useRestoreVehicleDataFromCheckoutSession = (checkoutSession: CheckoutSession, opened: boolean) => {
  const {
    state: { cart },
    actions: { updateApplicationContext, resetApplicationContext },
  } = useApplicationContext();
  const { createNewCartFromItems } = useCart();
  const { merchantStructure, loading: merchantStructureLoading } = useGetMerchantStructureById(
    checkoutSession.merchantId,
  );

  useEffect(() => {
    if (!merchantStructure) {
      return;
    }

    updateApplicationContext({ checkoutSessionId: checkoutSession.id });
    hydrateApplicationContextFromMerchantStructure(
      merchantStructure,
      checkoutSession.agentId,
      updateApplicationContext,
    );

    const cartItems = ItemMap.fromCheckoutSessionToItems(checkoutSession);
    createNewCartFromItems(cartItems, checkoutSession.purchaseCountry);
  }, [opened, merchantStructure, checkoutSession.id]);

  const isReady = !!cart?.items[0];
  const loading = opened && merchantStructureLoading && !merchantStructure;

  return { cart, isReady, loading, resetApplicationContext };
};

const hydrateApplicationContextFromMerchantStructure = (
  merchantStructure: MerchantStructure,
  agentId: string,
  updateApplicationContext: ReturnType<typeof useApplicationContext>['actions']['updateApplicationContext'],
) => {
  const merchantStructureEntity = MerchantStructureEntity.create({
    merchantStructure,
    agentId,
  });

  const selectedMerchant = merchantStructureEntity.getMerchant();
  const selectedChannel = merchantStructureEntity.getChannel();
  const selectedAgent = merchantStructureEntity.getAgent();

  if (!selectedMerchant || !selectedChannel || !selectedAgent) {
    console.error('Not able to restore checkout session merchant structure');
    return;
  }

  updateApplicationContext({
    selectedMerchant,
    selectedChannel,
    selectedAgent,
    selectedAgentHasError: false,
  });
};
